import {
  div,
  h1,
  button,
  span,
  img,
  fnstate,
  input,
  option,
  select,
  label,
  table,
  thead,
  tbody,
  tr,
  th,
  td
} from '@srfnstack/fntags'
import { createPaginatedList } from '../utils/pagination.js'

const REPUSH_STATUSES = [
  { value: '', label: 'All Eligible' },
  { value: 'READY_FOR_REVIEW', label: 'Ready for Review' },
  { value: 'PUSHED_TO_PRINTIFY', label: 'Pushed to Printify' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FAILED', label: 'Failed' }
]

const REPUBLISH_STATUSES = [
  { value: '', label: 'All Eligible' },
  { value: 'PUSHED_TO_PRINTIFY', label: 'Pushed to Printify' },
  { value: 'PUBLISHED', label: 'Published' }
]

const getStatusColor = (status) => {
  switch (status) {
    case 'READY_FOR_REVIEW': return 'badge-success'
    case 'PUBLISHED': return 'badge-info'
    case 'PUSHED_TO_PRINTIFY': return 'badge-secondary'
    case 'QUEUED_FOR_PUBLISH': return 'badge-accent'
    case 'FAILED': return 'badge-error'
    default: return 'badge-ghost'
  }
}

export const BulkOperations = () => {
  const actionFilter = fnstate('repush')
  const statusFilter = fnstate('')
  const productTypeFilter = fnstate('')
  const tenantFilter = fnstate('')
  const needsUpdateFilter = fnstate(false)
  const productTypes = fnstate([])
  const tenants = fnstate([])
  const selectedProducts = fnstate([])
  const sortCol = fnstate('created_at')
  const sortDir = fnstate('desc')

  const buildUrl = (offset, limit) => {
    const params = new URLSearchParams({ limit, offset, _: Date.now() })
    params.set('action', actionFilter())
    const s = statusFilter()
    if (s) params.set('status', s)
    const pt = productTypeFilter()
    if (pt) params.set('product_type', pt)
    const tid = tenantFilter()
    if (tid) params.set('tenant_id', tid)
    if (needsUpdateFilter()) params.set('needs_update', 'true')
    return `/api/product-concepts/bulk-operations?${params}`
  }

  // createPaginatedList carries the request-token guard this page never had:
  // loadMore used to append whatever came back, so changing a filter mid-flight
  // let the PREVIOUS filter's rows land in the new list. onReset clears the
  // selection, which was built from rows the new filter may not return; onPage
  // keeps the tenants dropdown, which rides along on the same response.
  const list = createPaginatedList({
    buildUrl,
    limit: 50,
    onReset: () => selectedProducts([]),
    onPage: (data) => {
      if (data.tenants && data.tenants.length > 0) {
        const newIds = data.tenants.map(t => t.id).join(',')
        const oldIds = tenants().map(t => t.id).join(',')
        if (newIds !== oldIds) tenants(data.tenants)
      }
    }
  })
  const { items, total, loading, loadingMore, sentinel } = list
  const fetchProducts = list.reload

  const setAction = (val) => {
    actionFilter(val)
    statusFilter('')
    fetchProducts()
  }
  const setStatus = (val) => { statusFilter(val); fetchProducts() }
  const setProductType = (val) => { productTypeFilter(val); fetchProducts() }
  const setTenant = (val) => { tenantFilter(val); fetchProducts() }
  const setNeedsUpdate = (val) => { needsUpdateFilter(val); fetchProducts() }

  fetch('/api/system/product-types').then(r => r.ok && r.json().then(types => productTypes(types)))

  const selectAllOnPage = () => {
    const ids = items().map(p => p.id)
    selectedProducts(ids)
  }

  const handleBulkAction = async () => {
    const sel = selectedProducts()
    if (sel.length === 0) return
    const action = actionFilter()
    const actionLabel = action === 'republish' ? 'republish to Etsy' : 'repush to Printify'
    if (!await window.confirm(`${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} ${sel.length} products?`)) return

    const endpoint = action === 'republish'
      ? '/api/product-concepts/bulk-publish-to-etsy'
      : '/api/product-concepts/bulk-push-to-printify'

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_ids: sel })
    })
    if (res.ok) {
      const data = await res.json()
      await window.alert(`Successfully queued ${data.count} products to ${actionLabel}!`)
      selectedProducts([])
      fetchProducts()
    } else {
      const err = await res.json()
      await window.alert('Failed: ' + (err.error || 'Unknown error'))
    }
  }

  const toggleSort = (col) => {
    if (sortCol() === col) {
      sortDir(sortDir() === 'asc' ? 'desc' : 'asc')
    } else {
      sortCol(col)
      sortDir(col === 'created_at' ? 'desc' : 'asc')
    }
    const sorted = [...items()].sort((a, b) => {
      let av = a[col] || ''
      let bv = b[col] || ''
      if (col === 'created_at') { av = new Date(av); bv = new Date(bv) }
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase() }
      if (av < bv) return sortDir() === 'asc' ? -1 : 1
      if (av > bv) return sortDir() === 'asc' ? 1 : -1
      return 0
    })
    items(sorted)
  }

  const sortHeader = (labelText, col) => {
    const sc = sortCol()
    const arrow = sc === col ? (sortDir() === 'asc' ? 'arrow_upward' : 'arrow_downward') : ''
    return th({
      class: 'cursor-pointer select-none hover:bg-base-200 transition-colors',
      onclick: () => toggleSort(col)
    },
    span({ class: 'flex items-center gap-1' },
      labelText,
      arrow ? span({ class: 'material-symbols-rounded text-sm' }, arrow) : ''
    )
    )
  }

  const renderRow = (p) => {
    const displayImage = p.source_asset_path || p.upscaled_image

    return tr({
      class: selectedProducts.bindAttr(sel => `hover:bg-base-200 cursor-pointer transition-colors ${sel.includes(p.id) ? 'bg-primary/5' : ''}`),
      onclick: () => { window.location.hash = `/product-review/${p.id}` }
    },
    td({ class: 'w-10' },
      input({
        type: 'checkbox',
        class: 'checkbox checkbox-primary checkbox-sm',
        checked: selectedProducts.bindAttr(sel => sel.includes(p.id)),
        onclick: (e) => {
          e.stopPropagation()
          const sel = selectedProducts()
          selectedProducts(sel.includes(p.id) ? sel.filter(id => id !== p.id) : [...sel, p.id])
        }
      })
    ),
    td({ class: 'w-12' },
      displayImage
        ? img({
          src: `/api/image?file=${encodeURIComponent(displayImage)}`,
          class: 'w-10 h-10 object-cover rounded border border-base-300'
        })
        : div({ class: 'w-10 h-10 bg-base-200 rounded flex items-center justify-center opacity-30' }, '?')
    ),
    td({ class: 'max-w-xs' },
      div({ class: 'font-medium text-sm truncate' }, p.design_title || 'Untitled')
    ),
    td(
      span({ class: 'text-xs opacity-60' }, p.tenant_name || '')
    ),
    td(
      span({ class: 'text-xs uppercase tracking-wider opacity-50 font-bold' }, p.product_type)
    ),
    td(
      div({ class: 'flex gap-1 items-center' },
        span({ class: `badge badge-sm ${getStatusColor(p.status)} font-bold border-none` }, p.status),
        p.needs_update ? span({ class: 'badge badge-sm badge-warning font-bold border-none' }, 'Update') : ''
      )
    ),
    td({ class: 'text-xs opacity-40' },
      new Date(p.created_at).toLocaleDateString()
    )
    )
  }

  fetchProducts()

  return div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)]' },
    div({ class: 'flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6' },
      div({ class: 'flex items-center gap-3' },
        h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Bulk Operations'),
        total.bindAs(t => t > 0 ? span({ class: 'badge badge-ghost font-mono' }, t) : span())
      ),
      div({ class: 'flex flex-wrap items-center gap-3 w-full md:w-auto' },
        // Action selector
        div({ class: 'join' },
          actionFilter.bindAs(action =>
            div({ class: 'join' },
              button({
                class: `btn btn-sm join-item ${action === 'repush' ? 'btn-primary' : 'btn-ghost'}`,
                onclick: () => setAction('repush')
              }, 'Repush'),
              button({
                class: `btn btn-sm join-item ${action === 'republish' ? 'btn-primary' : 'btn-ghost'}`,
                onclick: () => setAction('republish')
              }, 'Republish')
            )
          )
        ),
        // Status filter
        actionFilter.bindAs(action => select({
          class: 'select select-bordered select-sm',
          onchange: (e) => setStatus(e.target.value)
        },
        (action === 'republish' ? REPUBLISH_STATUSES : REPUSH_STATUSES)
          .map(o => option({ value: o.value, selected: statusFilter() === o.value }, o.label))
        )),
        // Product type filter
        productTypes.bindAs(types => select({
          class: 'select select-bordered select-sm',
          onchange: (e) => setProductType(e.target.value)
        },
        option({ value: '', selected: productTypeFilter() === '' }, 'All Types'),
        types.map(t => option({ value: t.keyword, selected: productTypeFilter() === t.keyword }, t.keyword))
        )),
        // Tenant filter
        tenants.bindAs(list => list.length > 0
          ? select({
            class: 'select select-bordered select-sm',
            onchange: (e) => setTenant(e.target.value)
          },
          option({ value: '', selected: tenantFilter() === '' }, 'All Tenants'),
          list.map(t => option({ value: t.id, selected: tenantFilter() === String(t.id) }, t.name))
          )
          : ''
        ),
        // Needs update toggle
        label({ class: 'flex items-center gap-2 cursor-pointer' },
          input({
            type: 'checkbox',
            class: 'checkbox checkbox-warning checkbox-sm',
            checked: needsUpdateFilter(),
            onchange: (e) => setNeedsUpdate(e.target.checked)
          }),
          span({ class: 'text-sm font-medium' }, 'Needs Update Only')
        ),
        button({ class: 'btn btn-outline btn-sm', onclick: fetchProducts }, 'Refresh')
      )
    ),

    // Bulk action bar
    selectedProducts.bindAs(sel => {
      if (sel.length === 0) return div()
      return div({ class: 'fixed bottom-12 left-1/2 -translate-x-1/2 bg-base-300 p-4 rounded-2xl shadow-2xl flex gap-4 items-center z-[150] border border-primary/30 backdrop-blur-md' },
        span({ class: 'font-bold text-sm px-2' }, `${sel.length} selected`),
        button({ class: 'btn btn-ghost btn-sm', onclick: selectAllOnPage }, 'Select All on Page'),
        actionFilter.bindAs(action =>
          button({
            class: action === 'republish' ? 'btn btn-success btn-sm' : 'btn btn-primary btn-sm',
            onclick: handleBulkAction
          }, action === 'republish' ? 'Republish to Etsy' : 'Repush to Printify')
        ),
        button({ class: 'btn btn-ghost btn-sm btn-circle', onclick: () => selectedProducts([]), title: 'Clear' }, span({ class: 'material-symbols-rounded text-[18px]' }, 'close'))
      )
    }),

    // Product table
    loading.bindAs(l => l
      ? div({ class: 'flex items-center justify-center h-64' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
      : items.bindAs(list => {
        if (list.length === 0) {
          return div({ class: 'bg-base-100 rounded-xl border border-base-300 p-12 text-center opacity-60 flex flex-col items-center gap-2 shadow-sm' },
            span({ class: 'material-symbols-rounded text-5xl' }, 'sync'),
            span('No products match your filters.')
          )
        }
        return div({ class: 'flex flex-col gap-2' },
          div({ class: 'overflow-x-auto bg-base-100 rounded-xl border border-base-300 shadow-sm' },
            table({ class: 'table table-sm' },
              thead(
                tr(
                  th({ class: 'w-10' },
                    input({
                      type: 'checkbox',
                      class: 'checkbox checkbox-primary checkbox-sm',
                      checked: selectedProducts.bindAttr(sel => sel.length === list.length && list.length > 0),
                      onclick: () => {
                        selectedProducts().length === list.length ? selectedProducts([]) : selectAllOnPage()
                      }
                    })
                  ),
                  th({ class: 'w-12' }, ''),
                  sortHeader('Title', 'design_title'),
                  sortHeader('Tenant', 'tenant_name'),
                  sortHeader('Type', 'product_type'),
                  sortHeader('Status', 'status'),
                  sortHeader('Created', 'created_at')
                )
              ),
              tbody(
                list.map(renderRow)
              )
            )
          ),
          loadingMore.bindAs(lm => lm
            ? div({ class: 'flex justify-center py-4' }, span({ class: 'loading loading-spinner loading-md text-primary' }))
            : div()
          ),
          sentinel
        )
      })
    )
  )
}
